// AcervoDoClube.js — o catálogo inteiro, dentro da página do Clube, para quem
// já assina.
//
// ================= POR QUE ELE TOMA O LUGAR DAS VANTAGENS =================
//
// Pedido do dono em 10/09/2026. "O que a assinatura dá" é argumento de venda,
// e quem já assina não precisa mais ser convencido: para essa pessoa a página
// do Clube passava a ser um folheto do que ela já comprou. O que ela quer ali é
// o acervo — então, com a assinatura ativa, a seção vira o acervo.
//
// ================= É O CATÁLOGO DE /livros, MENOS A LOJA =================
//
// Mesma busca (título ou autor), mesma faceta de categorias (o PainelFiltros e
// o painel de lá, reaproveitado, com a contagem por categoria), mesma
// paginação com o número na URL. O que sai é tudo o que é venda: preço,
// carrinho, ordenar por preço e o card com botão de compra (ver CardDoClube).
//
// SÓ ENTRA LIVRO COM ARQUIVO (`tem_pdf`). O acervo do Clube é "todo e-book com
// arquivo disponível" — a mesma regra de `Ebook::legivel()`, que é o que o
// leitor e o contador de acervo da vitrine usam. Mostrar aqui um título só
// impresso seria oferecer ao assinante um "Ler agora" que o leitor recusa.
//
// ================= A VOLTA DO LIVRO CAI NO ACERVO =================
//
// O acervo mora no meio de uma página longa. Sem cuidado, abrir um livro e
// apertar "voltar" devolvia a pessoa ao TOPO da página do Clube, a várias telas
// da grade onde ela estava. Ao abrir um card, a entrada atual do histórico
// ganha `#acervo`; na volta, a seção rola para a vista assim que os livros
// chegam — com a busca, a categoria e a página intactas, porque moram na URL.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BotaoCategorias } from "../produto/gridProduto/PainelFiltros";
import Pagination from "../utils/Pagination";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../hooks/useBarraGrudada";
import RevealStagger from "../ui/RevealStagger";
import CaixaVazia from "../ui/CaixaVazia";
import CardDoClube, { CardDoClubeSkeleton, nomesDosAutores } from "./CardDoClube";

const API_URL = process.env.REACT_APP_API;

// 12 fecha as duas larguras da grade (2 e 3 colunas) sem fileira pela metade.
const POR_PAGINA = 12;

// A ORDEM NÃO TEM MAIS SELETOR (11/09/2026): o acervo se percorre por busca e
// por categoria, e três opções de ordem eram três cliques para uma pergunta que
// ninguém fazia. As chaves continuam aqui porque `?ordem=` ainda é lido — link
// antigo não pode quebrar — e porque a ordenação abaixo depende delas.

const nomeCategoria = (c) => (typeof c === "string" ? c : c?.nome);

// "direito" acha "Direito" e "Dírêito": quem digita no celular raramente põe
// acento, e o título quase sempre tem.
const semAcento = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const temNota = (e) => (Number(e.avaliacoes_count) > 0 ? 1 : 0);

const AcervoDoClube = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ---- O estado dos filtros MORA NA URL (ver o cabeçalho) ----
  const params = new URLSearchParams(location.search);
  const busca = params.get("busca") || "";
  const categoriaSel = params.get("categoria") || "";
  // A ORDEM É FIXA desde 11/09/2026, com o seletor fora: do mais novo para o
  // mais antigo. `?ordem=` ainda é lido para não quebrar link antigo.
  const ordem = params.get("ordem") || "recentes";
  const paginaURL = parseInt(params.get("pagina"), 10) || 1;

  const [livros, setLivros] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  // O campo de busca tem estado próprio só para o cursor não brigar com a URL
  // a cada tecla; o valor que filtra é o da URL.
  const [texto, setTexto] = useState(busca);
  // A barrinha que filtra a LISTA de categorias no painel — não os livros.
  const [buscaCategoria, setBuscaCategoria] = useState("");
  // A barra de busca some quando o cabeçalho do site volta, pela mesma regra
  // das outras barras que grudam (useBarraGrudada, pedido de 11/09/2026).
  //
  // AQUI HAVIA a medição da altura desta barra, publicada como
  // `--altura-barra` para a coluna de categorias grudar logo abaixo dela. A
  // coluna virou popup e a medida ficou sem ninguém que a lesse.
  const { barraRef, sentinelaRef, esconder: esconderBarra } = useBarraGrudada();

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(false);
    (async () => {
      try {
        // O MESMO /ebook do catálogo, e não uma rota nova: a lista, os autores,
        // as categorias e a nota já vêm prontos ali, e uma segunda rota com
        // "o catálogo menos o preço" seria mais uma a manter em sincronia.
        const [rLivros, rCat] = await Promise.all([
          fetch(API_URL + "ebook/"),
          fetch(API_URL + "categorias"),
        ]);
        if (!rLivros.ok) throw new Error("acervo");
        const lista = await rLivros.json();
        const cats = rCat.ok ? await rCat.json() : [];
        if (!vivo) return;
        setLivros((Array.isArray(lista) ? lista : []).filter((e) => e?.tem_pdf));
        setCategorias(Array.isArray(cats) ? cats : []);
      } catch {
        if (vivo) setErro(true);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [tentativa]);

  // Escreve na URL. Filtro, busca e ordem novos invalidam a página em que a
  // pessoa estava (filtrar da página 7 com três páginas de resultado mostraria
  // grade vazia), então toda mudança que não é de página volta para a 1.
  //
  // Filtros SUBSTITUEM a entrada do histórico; trocar de página EMPILHA — é o
  // que faz o "voltar" do navegador desfazer a paginação, e não cada tecla.
  const mudar = (alteracoes, empilhar = false) => {
    const p = new URLSearchParams(location.search);
    Object.entries(alteracoes).forEach(([k, v]) => {
      if (v && !(k === "pagina" && Number(v) <= 1)) p.set(k, String(v));
      else p.delete(k);
    });
    if (!("pagina" in alteracoes)) p.delete("pagina");
    const qs = p.toString();
    navigate({ pathname: location.pathname, search: qs ? "?" + qs : "" }, { replace: !empilhar });
  };

  const nomesCategorias = useMemo(
    () => categorias.map(nomeCategoria).filter(Boolean),
    [categorias]
  );

  // ====== Busca ======
  // Título e TODOS os autores. `semCategoria` é o conjunto que passou por tudo
  // menos a categoria — é dele que sai a contagem da faceta (ver o mesmo
  // raciocínio, por extenso, em GridProduto.js).
  const termo = semAcento(busca).trim();
  const semCategoria = useMemo(() => {
    if (!termo) return livros;
    return livros.filter((e) =>
      semAcento(`${e.titulo || ""} ${nomesDosAutores(e.autores).join(" ")}`).includes(termo)
    );
  }, [livros, termo]);

  const contagemPorCategoria = useMemo(() => {
    const conta = new Map();
    semCategoria.forEach((e) => {
      const cats = Array.isArray(e.categorias) ? e.categorias : [];
      new Set(cats.map(nomeCategoria).filter(Boolean)).forEach((nome) => {
        conta.set(nome, (conta.get(nome) || 0) + 1);
      });
    });
    return conta;
  }, [semCategoria]);

  const filtrados = useMemo(() => {
    if (!categoriaSel) return semCategoria;
    return semCategoria.filter((e) =>
      (Array.isArray(e.categorias) ? e.categorias : []).some((c) => nomeCategoria(c) === categoriaSel)
    );
  }, [semCategoria, categoriaSel]);

  const ordenados = useMemo(() => {
    const arr = [...filtrados];
    if (ordem === "recentes") arr.sort((a, b) => Number(b.id) - Number(a.id));
    if (ordem === "avaliados") {
      // Quem tem nota vem antes de quem não tem, e só então a nota decide. Sem
      // isso, um livro com UMA avaliação 5 passaria na frente de um com
      // quarenta avaliações 4,8 — o desempate pela contagem cuida do resto.
      arr.sort((a, b) =>
        temNota(b) - temNota(a) ||
        (Number(b.avaliacoes_avg_nota) || 0) - (Number(a.avaliacoes_avg_nota) || 0) ||
        (Number(b.avaliacoes_count) || 0) - (Number(a.avaliacoes_count) || 0)
      );
    }
    if (ordem === "titulo") {
      arr.sort((a, b) => String(a.titulo || "").localeCompare(String(b.titulo || ""), "pt-BR", { sensitivity: "base" }));
    }
    return arr;
  }, [filtrados, ordem]);

  // ====== Paginação ======
  // Página fora da faixa cai na PRIMEIRA: ?pagina=9 quase sempre é link velho
  // de um filtro que encolheu, e a primeira nunca está errada.
  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / POR_PAGINA));
  const paginaAtual = paginaURL >= 1 && paginaURL <= totalPaginas ? paginaURL : 1;
  const inicio = (paginaAtual - 1) * POR_PAGINA;
  const itensPagina = ordenados.slice(inicio, inicio + POR_PAGINA);

  // ====== A volta de um livro (ver o cabeçalho) ======
  const rolouNaVolta = useRef(false);
  useEffect(() => {
    if (carregando || rolouNaVolta.current) return;
    rolouNaVolta.current = true;
    if (window.location.hash !== "#acervo") return;
    // Um quadro depois: a grade acabou de trocar os esqueletos pelos cards, e
    // rolar antes disso mira uma altura que ainda vai mudar.
    requestAnimationFrame(() => {
      document.getElementById("acervo")?.scrollIntoView({ block: "start" });
    });
  }, [carregando]);

  // `replaceState` direto, e não `navigate`: é só marcar a entrada ATUAL para a
  // volta, sem re-renderizar a página no meio do clique. O `state` do histórico
  // é preservado — é nele que o React Router guarda o índice da navegação.
  const marcarVolta = () => {
    try {
      const u = new URL(window.location.href);
      u.hash = "acervo";
      window.history.replaceState(window.history.state, "", u.toString());
    } catch { /* sem a marca, a volta só não rola até o acervo */ }
  };

  const propsFiltros = {
    categorias: nomesCategorias,
    categoriaSel,
    aoEscolherCategoria: (c) => mudar({ categoria: c }),
    contagemPorCategoria,
    totalSemCategoria: semCategoria.length,
    buscaCategoria,
    aoBuscarCategoria: setBuscaCategoria,
  };

  const chips = [];
  if (categoriaSel) chips.push({ id: "cat", texto: categoriaSel, limpar: () => mudar({ categoria: "" }) });
  if (busca.trim()) {
    chips.push({
      id: "busca",
      texto: `Busca: "${busca.trim()}"`,
      limpar: () => { setTexto(""); mudar({ busca: "" }); },
    });
  }

  const limparTudo = () => {
    setBuscaCategoria("");
    setTexto("");
    mudar({ categoria: "", busca: "" });
  };


  return (
    // `--topo-acervo` = onde a barra de busca gruda. São DOIS lugares:
    //
    //  - na página /clube, a borda de baixo do cabeçalho do site (a conta de
    //    --nav-h e --nav-topo está em GridProduto.js);
    //  - na aba Clube do PERFIL, logo abaixo da barra do nome, que também
    //    gruda. Lá o Perfil declara --topo-perfil e --altura-cabecalho-perfil,
    //    e o `var(..., fallback)` escolhe sozinho: a variável só existe dentro
    //    do perfil. Com a conta do site nos dois lugares, as duas barras
    //    grudavam na MESMA altura e a do acervo cobria a do nome.
    <section
      id="acervo"
      className="mt-8 scroll-mt-28 md:[--topo-acervo:calc(var(--topo-perfil,max(0px,calc(var(--nav-h)_+_var(--nav-topo)_-_40px)))_+_var(--altura-cabecalho-perfil,0px))]"
    >
      <h2 className="titulo-display text-[2rem] sm:text-[2.4rem] flex items-center gap-2">
        <i className="bi bi-collection-fill text-insigne-orange" /> O acervo do Clube
      </h2>
      <p className="text-sm text-white/60 mt-1.5 leading-relaxed">
        {carregando
          ? "Carregando o acervo..."
          : livros.length > 0
          ? `${livros.length} livros para ler agora, todos incluídos na sua assinatura.`
          : "Todo e-book do catálogo, incluído na sua assinatura."}
      </p>

      {/* ===== A BARRA DE BUSCA ACOMPANHA A ROLAGEM (desktop) =====
          Pedido de 11/09/2026: busca, ordem e os filtros ligados descem junto
          com a barra de categorias, como em /livros. Ela gruda na borda do
          cabeçalho do site, e as categorias grudam logo abaixo dela.

          Fundo opaco e `z-30` pelo mesmo motivo da faixa de /livros: sem
          fundo as capas passariam por baixo do campo de busca; sem camada, a
          grade — que vem depois no DOM — pintaria por cima. `-mx-3 px-3`
          estica o fundo para cobrir a sombra dura dos cards que se projeta
          para fora da coluna.

          Só do tablet para cima: no celular não há barra de categorias ao
          lado para acompanhar, e uma faixa presa no topo comeria a tela. */}
      {/* A sentinela leva o `mt-4` que era da barra: assim ela marca
          exatamente onde a barra nasce, e o "está grudada?" do useBarraGrudada
          não dispara 16px antes da hora. */}
      <div ref={sentinelaRef} aria-hidden="true" className="mt-4 h-0" />
      <div
        ref={barraRef}
        className={
          "md:sticky md:z-30 md:top-[var(--topo-acervo)] md:-mx-3 md:px-3 md:py-3 md:bg-insigne-bg " +
          CLASSES_SUMICO + " " + (esconderBarra ? CLASSES_ESCONDIDA : "")
        }
      >
      {/* ===== Busca + Categorias ===== */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[min(100%,220px)]">
          <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
          <input
            type="search"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              mudar({ busca: e.target.value });
            }}
            placeholder="Buscar por título ou autor..."
            aria-label="Buscar no acervo do Clube"
            className="foco-laranja w-full rounded-lg border border-line bg-white/5 py-2.5 pl-10 pr-3
                       text-sm text-white placeholder:text-white/40 outline-none
                       transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
          />
        </div>

        {/* ===== CATEGORIAS EM POPUP (pedido do dono, 11/09/2026) =====
            Elas ocupavam uma coluna de 220px à esquerda da grade — espaço fixo
            para um controle que a pessoa usa uma vez e fecha. Agora moram num
            popup ancorado no botão, no lugar onde estava o seletor de ordem
            (que saiu: quatro ordens para um acervo que se percorre por busca e
            categoria). A grade herdou a largura inteira.

            O componente é o MESMO de /livros e /autores desde 11/09/2026: o
            popup daqui era a primeira cópia da marcação, e foi dela que as
            outras duas telas nasceram. Sem `aoAbrirNoCelular` porque aqui não
            há bottom-sheet — o popup vale em qualquer largura. */}
        <BotaoCategorias filtros={propsFiltros} variante="largo" />
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {chips.map((chip) => (
            <button
              key={chip.id}
              onClick={chip.limpar}
              className="foco-laranja inline-flex items-center gap-2 rounded-controle border border-insigne-orange/60
                         bg-insigne-orange/10 pl-3 pr-2 py-1 text-xs text-insigne-orange
                         hover:bg-insigne-orange/20 transition-colors"
            >
              {chip.texto}
              <i className="bi bi-x-lg text-[10px]" />
            </button>
          ))}
          {chips.length > 1 && (
            <button onClick={limparTudo} className="text-xs text-white/55 underline hover:text-white transition-colors ml-1">
              limpar tudo
            </button>
          )}
        </div>
      )}
      </div>

      <div className="mt-3 md:mt-2">
          {!carregando && !erro && ordenados.length > 0 && (
            <p className="text-xs text-white/50 mb-3">
              Mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, ordenados.length)} de{" "}
              {ordenados.length} livro{ordenados.length === 1 ? "" : "s"}
            </p>
          )}

          {carregando ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 orfa-ao-centro">
              {Array.from({ length: POR_PAGINA }).map((_, i) => <CardDoClubeSkeleton key={i} />)}
            </div>
          ) : erro ? (
            <div className="bloco p-8 text-center">
              <p className="font-semibold">Não foi possível carregar o acervo agora.</p>
              <button
                onClick={() => setTentativa((n) => n + 1)}
                className="btn-solido mt-4 rounded-controle px-5 py-2 text-sm font-semibold"
              >
                Tentar de novo
              </button>
            </div>
          ) : ordenados.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14">
              <CaixaVazia />
              <h3 className="text-lg font-bold mt-6">
                Nada encontrado para "{busca.trim() || categoriaSel || "esses filtros"}"
              </h3>
              <p className="text-white/50 mt-1 text-sm">Tente outra busca ou outra categoria.</p>
              {chips.length > 0 && (
                <button onClick={limparTudo} className="btn-solido mt-5 rounded-controle px-5 py-2 text-sm font-semibold">
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              {/* `gap-y-6`, e não o `gap-y-12` de /livros: lá a folga existe
                  para o botão de carrinho pendurado na borda do card, e este
                  card não tem carrinho. */}
              <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 orfa-ao-centro">
                {itensPagina.map((livro) => (
                  <RevealStagger.Item key={livro.id} className="h-full">
                    <CardDoClube livro={livro} onAbrir={marcarVolta} />
                  </RevealStagger.Item>
                ))}
              </RevealStagger>

              {/* `rolarAte`: trocar de página leva ao topo do ACERVO, e não ao
                  topo da página do Clube, que fica várias telas acima. */}
              <Pagination
                pagina={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={(n) => mudar({ pagina: n }, true)}
                rolarAte="acervo"
              />
            </>
          )}
      </div>

    </section>
  );
};

export default AcervoDoClube;
